import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { type TaxAnalysis } from '@popety-io/types'
import {
  TaxBurdenEvolutionChart,
  type TaxBurdenEvolutionChartRef,
} from '../charts/TaxBurdenEvolutionChart'
import {
  TaxAmountEvolutionChart,
  type TaxAmountEvolutionChartRef,
} from '../charts/TaxAmountEvolutionChart'
import {
  TaxRateEvolutionChart,
  type TaxRateEvolutionChartRef,
} from '../charts/TaxRateEvolutionChart'
import { ChartHeader } from '../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../utils'
import {
  TabContentContainer,
  ChartGrid,
  ChartContainer,
} from '../TaxSection.styles'

export interface TrendsTabProps {
  data: TaxAnalysis | null
  t: TFunction
}

export const TrendsTab: React.FC<TrendsTabProps> = ({ data, t }) => {
  const taxBurdenChartRef = useRef<TaxBurdenEvolutionChartRef>(null)
  const taxAmountChartRef = useRef<TaxAmountEvolutionChartRef>(null)
  const taxRateChartRef = useRef<TaxRateEvolutionChartRef>(null)

  if (!data?.time_series || data.time_series.length === 0) {
    return (
      <TabContentContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('states.noHistoricalData')}
        </div>
      </TabContentContainer>
    )
  }

  return (
    <TabContentContainer>
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.taxBurdenEvolution.title')}
            subtitle={t('charts.taxBurdenEvolution.subtitle')}
            onDownload={() => {
              const dataUrl = taxBurdenChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.taxBurdenEvolution.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <TaxBurdenEvolutionChart
            ref={taxBurdenChartRef}
            data={data.time_series}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.taxAmountEvolution.title')}
            subtitle={t('charts.taxAmountEvolution.subtitle')}
            onDownload={() => {
              const dataUrl = taxAmountChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.taxAmountEvolution.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <TaxAmountEvolutionChart
            ref={taxAmountChartRef}
            data={data.time_series}
            t={t}
          />
        </ChartContainer>
        {/* Tax Rate Evolution Chart */}
        {data.rate_time_series && data.rate_time_series.length > 0 && (
          <ChartContainer>
            <ChartHeader
              title={t('charts.taxRateEvolution.title')}
              subtitle={t('charts.taxRateEvolution.subtitle')}
              onDownload={() => {
                const dataUrl = taxRateChartRef.current?.exportChart()
                downloadChartAsPng(
                  dataUrl,
                  sanitizeFilename(t('charts.taxRateEvolution.title'))
                )
              }}
              downloadLabel={t('common.downloadChart')}
            />
            <TaxRateEvolutionChart
              ref={taxRateChartRef}
              data={data.rate_time_series}
              t={t}
            />
          </ChartContainer>
        )}
      </ChartGrid>
    </TabContentContainer>
  )
}
